import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Phone, Instagram, Mail, ArrowRight } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const trackCTA = (label) => { if (window.gtag) window.gtag("event", "cta_click", { event_category: "engagement", event_label: label }); };

const LOGO_URL = "https://customer-assets-4nw71qhi.emergentagent.net/job_turnkey-deploy/artifacts/8dz90kua_Cd-logo-04.PNG";

const navLinks = [
  { to: "/operators", label: "Kitchens" },
  { to: "/landlords", label: "Property Owners" },
  { to: "/sites", label: "Sites" },
  { to: "/how-it-works", label: "How It Works" },
  { to: "/about", label: "About" },
  { to: "/gallery", label: "Gallery" },
  { to: "/contact", label: "Apply" },
];

export default function Layout({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const goTo = (link) => (e) => {
    if (!link.hash) { setMobileOpen(false); return; }
    e.preventDefault();
    setMobileOpen(false);
    if (location.pathname !== "/") {
      navigate("/#" + link.hash);
    } else {
      document.getElementById(link.hash)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#FAFAFA" }}>
      {/* Navigation */}
      <nav
        data-testid="main-navigation"
        className="fixed top-0 w-full z-50 transition-all duration-500"
        style={{
          backgroundColor: scrolled ? "rgba(255,255,255,0.92)" : "transparent",
          backdropFilter: scrolled ? "blur(20px)" : "none",
          borderBottom: scrolled ? "1px solid rgba(26,29,35,0.06)" : "1px solid transparent",
        }}
      >
        <div className="w-full max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24 flex items-center justify-between h-20">
          <Link to="/" data-testid="nav-logo" className="flex items-center flex-shrink-0">
            <img src={LOGO_URL} alt="PadSite Partners" className="h-14 md:h-16 w-auto" />
          </Link>

          <div className="hidden lg:flex items-center gap-4 xl:gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                onClick={goTo(link)}
                data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-sm font-medium tracking-wide whitespace-nowrap transition-all duration-300"
                style={{
                  color: location.pathname === link.to ? "#d4782f" : (scrolled ? "#1A1D23" : "#FFFFFF"),
                  fontFamily: "'Space Grotesk', sans-serif",
                }}
                onMouseEnter={(e) => { e.target.style.color = "#d4782f"; }}
                onMouseLeave={(e) => { if (location.pathname !== link.to) e.target.style.color = scrolled ? "#1A1D23" : "#FFFFFF"; }}
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/contact"
              data-testid="nav-get-started"
              onClick={() => trackCTA("nav_get_started")}
              className="px-5 py-2.5 text-xs uppercase tracking-[0.1em] font-bold whitespace-nowrap transition-all glow-btn rounded-none"
              style={{ backgroundColor: "#d4782f", color: "#FFFFFF" }}
            >
              Get Started
            </Link>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-3 -mr-3"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
            style={{ color: scrolled ? "#1A1D23" : "#FFFFFF" }}
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        <AnimatePresence>
          {mobileOpen && (
            <motion.div
              data-testid="mobile-menu"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden overflow-hidden"
              style={{ backgroundColor: "#FFFFFF" }}
            >
              <div className="px-6 py-4 flex flex-col">
                {navLinks.map((link) => (
                  <Link
                    key={link.to}
                    to={link.to}
                    data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="block py-3 text-base font-medium tracking-wide"
                    style={{ color: location.pathname === link.to ? "#d4782f" : "#1A1D23", fontFamily: "'Space Grotesk', sans-serif" }}
                    onClick={goTo(link)}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      <main className="pb-16 md:pb-0">{children}</main>

      {/* Footer */}
      <footer data-testid="site-footer" style={{ backgroundColor: "#2d2d2d" }}>
        <div className="w-full max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-24 py-16 md:py-20">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-16">
            <div>
              <Link to="/"><img src={LOGO_URL} alt="PadSite Partners" className="h-10 w-auto mb-5" /></Link>
              <p className="text-sm leading-relaxed max-w-xs" style={{ color: "#A1A5B0" }}>
                Turnkey modular kitchen units. Engineered, inspected, and ready for operators.
              </p>
            </div>
            <div>
              <h4 className="text-xs uppercase tracking-[0.2em] font-semibold mb-5" style={{ color: "#d4782f", fontFamily: "'Space Grotesk', sans-serif" }}>Navigation</h4>
              <div className="flex flex-col gap-1">
                {navLinks.map((link) => (
                  <Link key={link.to} to={link.to} onClick={goTo(link)} data-testid={`footer-${link.label.toLowerCase().replace(/\s+/g, "-")}`} className="block py-3 text-sm transition-colors" style={{ color: "#A1A5B0" }}
                    onMouseEnter={(e) => (e.target.style.color = "#d4782f")} onMouseLeave={(e) => (e.target.style.color = "#A1A5B0")}>{link.label}</Link>
                ))}
              </div>
            </div>
            <div>
              <h4 className="text-xs uppercase tracking-[0.2em] font-semibold mb-5" style={{ color: "#d4782f", fontFamily: "'Space Grotesk', sans-serif" }}>Contact</h4>
              <p className="text-sm mb-1" style={{ color: "#A1A5B0" }}>Atlanta, Georgia</p>
              <p className="text-sm mb-1" style={{ color: "#A1A5B0" }}>Toll-Free: <a href="tel:18884717237" className="inline-block py-2.5" style={{ color: "#d4782f" }}>1-888-471-PADS (7237)</a></p>
              <p className="text-sm mb-1" style={{ color: "#A1A5B0" }}>Local: <a href="tel:17705890723" className="inline-block py-2.5" style={{ color: "#d4782f" }}>770-589-0PAD (0723)</a></p>
              <p className="text-sm mb-3" style={{ color: "#A1A5B0" }}>Email: <a href="mailto:info@padsitepartners.com" data-testid="footer-email-link" className="inline-block py-2.5" style={{ color: "#d4782f" }}>info@padsitepartners.com</a></p>
              <Link to="/contact" className="text-sm font-medium" style={{ color: "#d4782f" }} data-testid="footer-contact-cta">Get In Touch</Link>
              <a href="https://www.instagram.com/PadsiteUSA" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm font-medium mt-3 transition-colors" style={{ color: "#A1A5B0" }}
                onMouseEnter={(e) => (e.currentTarget.style.color = "#d4782f")} onMouseLeave={(e) => (e.currentTarget.style.color = "#A1A5B0")}>
                <Instagram size={18} /> @PadsiteUSA
              </a>
            </div>
          </div>
          <div className="mt-12 pt-8 border-t flex justify-between items-center" style={{ borderTopColor: "rgba(161,165,176,0.06)" }}>
            <p className="text-xs" style={{ color: "rgba(161,165,176,0.4)" }}>&copy; {new Date().getFullYear()} PadSite Partners. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* Sticky Bottom Bar — primary conversion action */}
      <div className="fixed bottom-0 left-0 right-0 z-40" style={{ backgroundColor: "rgba(45,45,45,0.97)", backdropFilter: "blur(16px)", borderTop: "1px solid rgba(212,120,47,0.3)" }} data-testid="sticky-action-bar">
        <div className="w-full max-w-screen-2xl mx-auto px-3 md:px-12 lg:px-24 py-2.5 md:py-3 flex items-center justify-center gap-2 md:gap-4">
          <Link to="/sites" onClick={() => trackCTA("sticky_find_location")} data-testid="sticky-find-location"
            className="flex-1 md:flex-none inline-flex items-center justify-center gap-2 px-4 md:px-10 py-3 text-[11px] md:text-sm uppercase tracking-[0.1em] font-bold glow-btn rounded-none transition-all"
            style={{ backgroundColor: "#d4782f", color: "#FFFFFF" }}>
            Find Your Next Location <ArrowRight size={15} />
          </Link>
          <Link to="/contact?tab=landlord" onClick={() => trackCTA("sticky_have_land")} data-testid="sticky-have-land"
            className="hidden sm:inline-flex items-center px-4 py-3 text-[10px] md:text-xs uppercase tracking-[0.1em] font-bold rounded-none border transition-all"
            style={{ borderColor: "rgba(255,255,255,0.25)", color: "#FFFFFF" }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#d4782f"; }} onMouseLeave={(e) => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.25)"; }}>
            Have Land?
          </Link>
          <a href="tel:18884717237" onClick={() => trackCTA("phone_click")} data-testid="sticky-bar-call" aria-label="Call PadSite Partners"
            className="inline-flex items-center justify-center p-3 rounded-none border transition-all"
            style={{ borderColor: "rgba(255,255,255,0.18)", color: "rgba(255,255,255,0.8)" }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#d4782f"; }} onMouseLeave={(e) => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.18)"; }}>
            <Phone size={16} />
          </a>
          <a href="mailto:info@padsitepartners.com" onClick={() => trackCTA("email_click")} data-testid="sticky-bar-email" aria-label="Email PadSite Partners"
            className="inline-flex items-center justify-center p-3 rounded-none border transition-all"
            style={{ borderColor: "rgba(255,255,255,0.18)", color: "rgba(255,255,255,0.8)" }}
            onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#d4782f"; }} onMouseLeave={(e) => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.18)"; }}>
            <Mail size={16} />
          </a>
        </div>
      </div>
    </div>
  );
}
